// client/src/components/command/ReviewTab.jsx
// Order review table with filters, search, and expandable per-line-item validation.

import React, { useState } from "react";
import { useCommandOrders, useCommandStats } from "../../hooks/useCommand";
import CommandStatsCards from "./CommandStatsCards";
import { formatEasternDate } from "../../utils/timezone";

const s = {
    filters: {
        display: "flex", gap: "8px", marginBottom: "16px", flexWrap: "wrap",
        alignItems: "center",
    },
    filterBtn: (active) => ({
        padding: "6px 14px", borderRadius: "6px", border: "none", cursor: "pointer",
        fontSize: "12px", fontWeight: active ? 700 : 500,
        background: active ? "linear-gradient(135deg, #8b5cf6, #6366f1)" : "rgba(255,255,255,0.06)",
        color: active ? "#fff" : "#94a3b8",
        transition: "all 0.2s",
    }),
    searchInput: {
        padding: "6px 12px", borderRadius: "6px", border: "1px solid rgba(139, 92, 246, 0.2)",
        background: "#0d1117", color: "#e2e8f0", fontSize: "12px", outline: "none",
        marginLeft: "auto", width: "200px",
    },
    table: {
        width: "100%", borderCollapse: "separate", borderSpacing: 0,
        background: "linear-gradient(135deg, #1a1f2e 0%, #0d1117 100%)",
        borderRadius: "12px", overflow: "hidden",
        border: "1px solid rgba(139, 92, 246, 0.1)",
    },
    th: {
        padding: "10px 14px", textAlign: "left", fontSize: "11px", fontWeight: 700,
        textTransform: "uppercase", letterSpacing: "0.5px",
        color: "#64748b", background: "rgba(139, 92, 246, 0.05)",
        borderBottom: "1px solid rgba(139, 92, 246, 0.1)",
    },
    td: {
        padding: "10px 14px", fontSize: "13px", color: "#e2e8f0",
        borderBottom: "1px solid rgba(255,255,255,0.03)",
    },
    badge: (color) => ({
        display: "inline-block", padding: "2px 8px", borderRadius: "4px",
        fontSize: "11px", fontWeight: 600,
        background: color === "red" ? "rgba(239,68,68,0.15)" :
            color === "green" ? "rgba(16,185,129,0.15)" :
                color === "yellow" ? "rgba(245,158,11,0.15)" :
                    "rgba(139,92,246,0.15)",
        color: color === "red" ? "#ef4444" :
            color === "green" ? "#10b981" :
                color === "yellow" ? "#f59e0b" : "#8b5cf6",
    }),
    pagination: {
        display: "flex", justifyContent: "center", gap: "8px", marginTop: "16px",
        alignItems: "center",
    },
    pageBtn: (active) => ({
        padding: "6px 12px", borderRadius: "6px", border: "none", cursor: "pointer",
        fontSize: "12px", fontWeight: active ? 700 : 500,
        background: active ? "#8b5cf6" : "rgba(255,255,255,0.06)",
        color: active ? "#fff" : "#94a3b8",
    }),
    noData: { padding: "40px", textAlign: "center", color: "#64748b", fontSize: "14px" },
    expandArrow: (expanded) => ({
        display: "inline-block",
        marginRight: "6px",
        fontSize: "10px",
        transition: "transform 0.15s",
        transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
        cursor: "pointer",
        color: "#8b5cf6",
    }),
    expandedRow: {
        background: "rgba(139, 92, 246, 0.03)",
    },
    subTable: {
        width: "100%", borderCollapse: "collapse", margin: "4px 0",
    },
    subTh: {
        padding: "6px 10px", textAlign: "left", fontSize: "10px", fontWeight: 700,
        textTransform: "uppercase", letterSpacing: "0.3px",
        color: "#64748b", borderBottom: "1px solid rgba(139, 92, 246, 0.1)",
    },
    subTd: {
        padding: "6px 10px", fontSize: "12px", color: "#e2e8f0",
        borderBottom: "1px solid rgba(255,255,255,0.02)",
    },
};

// Row background color based on line-item validation status
function lineItemRowBg(item) {
    if (item.skip_reason === "already_cancelled") return "rgba(100, 116, 139, 0.08)";
    if (item.skip_reason) return "rgba(100, 116, 139, 0.05)";
    if (item.should_cancel) return "rgba(239, 68, 68, 0.06)";
    return "rgba(16, 185, 129, 0.04)";
}

function lineItemAction(item, locationLabel = "") {
    if (item.skip_reason === "already_cancelled") return { text: "SKIPPED (already cancelled)", color: "#94a3b8" };
    if (item.skip_reason === "sku_not_found_in_shopify") {
        const loc = String(locationLabel || "").trim();
        if (loc && loc !== "—") {
            return { text: `KEEP (${loc} assigned)`, color: "#94a3b8" };
        }
        return { text: "KEEP (SKU not in Shopify)", color: "#94a3b8" };
    }
    if (item.skip_reason) return { text: `SKIP (${item.skip_reason})`, color: "#94a3b8" };
    if (item.should_cancel) return { text: "CANCEL", color: "#ef4444" };
    return { text: "KEEP", color: "#10b981" };
}

function normalizeSku(value) {
    return String(value || "").trim().toUpperCase();
}

function buildSkuLocationFallbackMap(fulfillmentOrders = []) {
    const map = new Map();
    for (const fo of (fulfillmentOrders || [])) {
        const locName = fo?.assignedLocationName || fo?.assigned_location_name || "";
        const locId = fo?.assignedLocationId || fo?.assigned_location_id || "";
        const label = locName || locId || "";
        if (!label) continue;

        const lineItems = fo?.lineItems || fo?.line_items || [];
        for (const li of lineItems) {
            const skuKey = normalizeSku(li?.sku);
            if (!skuKey) continue;
            if (!map.has(skuKey)) map.set(skuKey, new Set());
            map.get(skuKey).add(label);
        }
    }
    return map;
}

function resolveLineItemLocationLabel(item, fallbackMap) {
    if (item.assigned_location_name) return item.assigned_location_name;
    const skuKey = normalizeSku(item.sku);
    if (!skuKey || !fallbackMap?.has(skuKey)) return "—";

    const labels = [...fallbackMap.get(skuKey)];
    if (labels.length === 1) return labels[0];
    return `${labels.length} locations`;
}

// Summarise locations across all line-item validation entries
function locationSummary(lineItemValidation) {
    if (!lineItemValidation || lineItemValidation.length === 0) return null;
    const uniqueLocations = new Map();
    for (const li of lineItemValidation) {
        if (li.assigned_location_name && li.assigned_location_id) {
            uniqueLocations.set(li.assigned_location_id, li.assigned_location_name);
        }
    }
    if (uniqueLocations.size === 0) return null;
    if (uniqueLocations.size === 1) return [...uniqueLocations.values()][0];
    return `${uniqueLocations.size} locations`;
}

const filterOptions = [
    { key: "all", label: "All" },
    { key: "should_cancel", label: "To Cancel" },
    { key: "mismatch", label: "Mismatched" },
    { key: "location_ok", label: "Location OK" },
    { key: "not_found", label: "Not Found" },
    { key: "unvalidated", label: "Unvalidated" },
    { key: "executed", label: "Executed" },
];

const SortHeader = ({ label, column, sort, dir, onSort, style }) => {
    const active = sort === column;
    const arrow = active ? (dir === "asc" ? " ↑" : " ↓") : " ↕";
    return (
        <th style={{ ...style, cursor: "pointer", userSelect: "none" }}
            onClick={() => onSort(column)}>
            {label}<span style={{ opacity: active ? 1 : 0.3, fontSize: "10px" }}>{arrow}</span>
        </th>
    );
};

// Sub-table showing per-line-item validation details
function LineItemSubTable({ items, fulfillmentOrders = [] }) {
    if (!items || items.length === 0) {
        return <div style={{ padding: "10px 20px", color: "#64748b", fontSize: "12px" }}>No line-item validation data.</div>;
    }
    const fallbackMap = buildSkuLocationFallbackMap(fulfillmentOrders);
    return (
        <table style={s.subTable}>
            <thead>
                <tr>
                    <th style={s.subTh}>SKU</th>
                    <th style={s.subTh}>Product</th>
                    <th style={s.subTh}>Qty</th>
                    <th style={s.subTh}>SH Status</th>
                    <th style={s.subTh}>Location</th>
                    <th style={s.subTh}>Mismatch</th>
                    <th style={s.subTh}>Action</th>
                </tr>
            </thead>
            <tbody>
                {items.map((item, i) => {
                    const resolvedLocationLabel = resolveLineItemLocationLabel(item, fallbackMap);
                    const action = lineItemAction(item, resolvedLocationLabel);
                    return (
                        <tr key={item.shiphero_line_item_id || i} style={{ background: lineItemRowBg(item) }}>
                            <td style={{ ...s.subTd, fontFamily: "monospace", fontSize: "11px" }}>
                                {item.sku || "—"}
                            </td>
                            <td style={{ ...s.subTd, maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                title={item.product_name}>
                                {item.product_name || "—"}
                            </td>
                            <td style={s.subTd}>{item.quantity}</td>
                            <td style={s.subTd}>
                                <span style={s.badge(
                                    item.shiphero_fulfillment_status === "cancelled" || item.shiphero_fulfillment_status === "canceled"
                                        ? "purple"
                                        : item.shiphero_fulfillment_status === "pending" ? "yellow" : "green"
                                )}>
                                    {item.shiphero_fulfillment_status || "pending"}
                                </span>
                            </td>
                            <td style={s.subTd}>
                                {resolvedLocationLabel}
                            </td>
                            <td style={s.subTd}>
                                {item.location_mismatch
                                    ? <span style={s.badge("red")}>MISMATCH</span>
                                    : item.skip_reason
                                        ? <span style={s.badge("purple")}>—</span>
                                        : <span style={s.badge("green")}>OK</span>}
                            </td>
                            <td style={{ ...s.subTd, fontWeight: 700, color: action.color, fontSize: "11px" }}>
                                {action.text}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

export default function ReviewTab() {
    const { orders, total, page, pages, loading, filter, search, sort, dir, setPage, setFilter, setSearch, toggleSort } = useCommandOrders();
    const { stats } = useCommandStats(5000);
    const [expandedIds, setExpandedIds] = useState(new Set());

    const toggleExpand = (id) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    return (
        <div>
            <CommandStatsCards stats={stats} />

            <div style={s.filters}>
                {filterOptions.map(f => (
                    <button key={f.key} style={s.filterBtn(filter === f.key)} onClick={() => { setFilter(f.key); setPage(1); }}>
                        {f.label}
                    </button>
                ))}
                <input style={s.searchInput} placeholder="Search order # or ID..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>

            {loading && orders.length === 0 ? (
                <div style={s.noData}>Loading orders...</div>
            ) : orders.length === 0 ? (
                <div style={s.noData}>No orders found. Run Fetch first.</div>
            ) : (
                <>
                    <table style={s.table}>
                        <thead>
                            <tr>
                                <SortHeader label="Order #" column="order_number" sort={sort} dir={dir} onSort={toggleSort} style={s.th} />
                                <th style={s.th}>Status</th>
                                <th style={s.th}>Location</th>
                                <th style={s.th}>Mismatch</th>
                                <th style={s.th}>Cancel?</th>
                                <th style={s.th}>Executed</th>
                                <SortHeader label="Date" column="order_date" sort={sort} dir={dir} onSort={toggleSort} style={s.th} />
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map(o => {
                                const lineItems = o.line_item_validation || [];
                                const isExpanded = expandedIds.has(o.id);
                                const hasLineItems = lineItems.length > 0;
                                const locSummary = locationSummary(lineItems);
                                const cancelCount = lineItems.filter(li => li.should_cancel).length;
                                const keepCount = lineItems.filter(li => !li.should_cancel && !li.skip_reason).length;

                                return (
                                    <React.Fragment key={o.id}>
                                        <tr style={{ opacity: loading ? 0.5 : 1, cursor: hasLineItems ? "pointer" : "default" }}
                                            onClick={() => hasLineItems && toggleExpand(o.id)}>
                                            <td style={s.td}>
                                                {hasLineItems && <span style={s.expandArrow(isExpanded)}>&#9654;</span>}
                                                <strong>{o.order_number || "—"}</strong>
                                                <br /><span style={{ color: "#64748b", fontSize: "11px" }}>{o.shiphero_id?.slice(-12)}</span>
                                            </td>
                                            <td style={s.td}>{o.fulfillment_status || "pending"}</td>
                                            <td style={s.td}>
                                                {o.validation_checked ? (
                                                    locSummary ? (
                                                        <span title={`Expected: ${o.expected_location_id || "?"}`}>
                                                            {locSummary}
                                                        </span>
                                                    ) : (
                                                        <span title={`ID: ${o.assigned_location_id || "?"}`}>
                                                            {o.assigned_location_name || o.assigned_location_id || "—"}
                                                        </span>
                                                    )
                                                ) : (
                                                    <span style={s.badge("purple")}>Unchecked</span>
                                                )}
                                            </td>
                                            <td style={s.td}>
                                                {o.location_mismatch ? <span style={s.badge("red")}>MISMATCH</span> :
                                                    o.validation_checked ? <span style={s.badge("green")}>OK</span> :
                                                        <span style={s.badge("purple")}>—</span>}
                                            </td>
                                            <td style={s.td}>
                                                {o.should_cancel ? (
                                                    <span style={s.badge("yellow")}>
                                                        Yes{cancelCount > 0 ? ` (${cancelCount}/${lineItems.length} items)` : ""}
                                                    </span>
                                                ) : (
                                                    <span style={{ color: "#64748b" }}>No</span>
                                                )}
                                            </td>
                                            <td style={s.td}>
                                                {o.execution_processed ? (
                                                    <span style={s.badge(o.cancel_status === "success" ? "green" : "red")}>
                                                        {o.cancel_status}
                                                    </span>
                                                ) : "—"}
                                            </td>
                                            <td style={{ ...s.td, fontSize: "11px", color: "#94a3b8" }}>
                                                {o.order_date ? formatEasternDate(o.order_date) : "—"}
                                            </td>
                                        </tr>
                                        {isExpanded && (
                                            <tr style={s.expandedRow}>
                                                <td colSpan={7} style={{ padding: "4px 20px 12px 30px" }}>
                                                    <div style={{ fontSize: "11px", color: "#94a3b8", marginBottom: "6px" }}>
                                                        Line Items: {lineItems.length} total
                                                        {cancelCount > 0 && <span style={{ color: "#ef4444", marginLeft: "8px" }}>{cancelCount} to cancel</span>}
                                                        {keepCount > 0 && <span style={{ color: "#10b981", marginLeft: "8px" }}>{keepCount} to keep</span>}
                                                    </div>
                                                    <LineItemSubTable
                                                        items={lineItems}
                                                        fulfillmentOrders={o.fulfillment_orders || []}
                                                    />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>

                    <div style={s.pagination}>
                        <button style={s.pageBtn(false)} onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}>Prev</button>
                        <span style={{ color: "#94a3b8", fontSize: "12px" }}>Page {page}/{pages} ({total.toLocaleString()} orders)</span>
                        <button style={s.pageBtn(false)} onClick={() => setPage(Math.min(pages, page + 1))} disabled={page >= pages}>Next</button>
                    </div>
                </>
            )}
        </div>
    );
}


